import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, message, Row, Col, Grid, Space, Popconfirm, Upload, Avatar } from 'antd';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, EditOutlined, CameraOutlined } from '@ant-design/icons';
import { updateContact } from '../../Messages/MessagesServices';
import ContactsServices from '../../Contacts/ContactsServices';
import { uploadProfileFile } from '../../Profile/ProfileServices';
import { resolveBackendMessage, getBackendMessageOr } from '../../../Utils/apiMessages';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';
import ImgCrop from 'antd-img-crop';
import { useNativeFileUpload } from '../../../Hooks/useNativeFileUpload';
import { shouldUseNativePicker } from '../../../Utils/nativeFileUpload';
import { useFileUploadPolicy } from '../../../Hooks/useFileUploadPolicy';
import { setupNativeUploadIntegration } from '../../../Utils/nativeUploadIntegration';
import ResponsiveModal from '../ResponsiveModal/ResponsiveModal';

const { Option } = Select;
const { useBreakpoint } = Grid;

const ContactEditModal = ({ visible, onCancel, contact, onSuccess }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [deletingProfilePicture, setDeletingProfilePicture] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(contact?.picture_url || undefined);
    const [photoUploadId, setPhotoUploadId] = useState(null);
    const { policy } = useFileUploadPolicy();
    const isNative = shouldUseNativePicker();
    const { handleError } = useErrorHandler();
    const screens = useBreakpoint();

    // Initialize native file upload hook
    const { uploading: uploadingPhoto, handleNativeImagePick, beforeUpload } = useNativeFileUpload({
        uploadFunction: uploadProfileFile,
        policy,
        imagesOnly: true,
        onSuccess: (processedFile, uploadResponse) => {
            const url = uploadResponse?.data?.file_url;
            if (url) {
                setPhotoUrl(url);
                setPhotoUploadId(uploadResponse?.data?.id || null);
                const successMessage = getBackendMessageOr(t, uploadResponse, 'upload_success');
                if (successMessage) {
                    message.success(successMessage);
                }
            }
        },
    });

    // Close fullscreen modal with mobile system back instead of navigating away
    useMobileFullscreenModalBackHandler(visible && screens.xs, onCancel);

    // Setup native upload integration on mobile
    useEffect(() => {
        if (isNative) {
            const cleanup = setupNativeUploadIntegration();
            return cleanup;
        }
    }, [isNative]);

    useEffect(() => {
        if (contact) {
            setPhotoUrl(contact.picture_url || undefined);
            form.setFieldsValue({
                contact_type: contact.contact_type || 'customer',
                first_name: contact.first_name || '',
                last_name: contact.last_name || '',
                picture_url: contact.picture_url || '',
                locale: contact.locale || '',
                timezone: contact.timezone || 0,
                gender: contact.gender || '',
                email: contact.email || '',
                phone: contact.phone || '',
                note: contact.note || ''
            });
        }
    }, [contact, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // If photo was uploaded, use the URL from the upload
            if (photoUrl && photoUploadId) {
                values.picture_url = photoUrl;
            }

            const payload = await updateContact(contact.id, values);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            onCancel();
            if (typeof onSuccess === 'function') {
                try {
                    onSuccess();
                } catch (e) { }
            }
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;

    const modalWidth = screens.xs ? '100%' : 800;

    const mobileStyles = screens.xs ? {
        maxWidth: '100vw',
        margin: 0,
        top: 0,
        padding: 0,
        height: '100vh',
    } : {};

    const bodyStyle = screens.xs ? {
        padding: '12px 8px',
        maxHeight: 'calc(100vh - 55px)',
        overflowY: 'auto',
        overflowX: 'hidden',
    } : {};

    const rowStyle = {
        marginLeft: 0,
        marginRight: 0,
    };

    return (
        <ResponsiveModal
            title={t('edit_contact_information')}
            open={visible}
            onCancel={onCancel}
            destroyOnClose
            desktopWidth={800}
            footer={
                <Button
                    type="primary"
                    block
                    onClick={handleSubmit}
                    loading={loading}
                >
                    {t('update_contact')}
                </Button>
            }
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    contact_type: contact?.contact_type || 'customer',
                    first_name: contact?.first_name || '',
                    last_name: contact?.last_name || '',
                    picture_url: contact?.picture_url || '',
                    locale: contact?.locale || '',
                    timezone: contact?.timezone || 0,
                    gender: contact?.gender || '',
                    email: contact?.email || '',
                    phone: contact?.phone || '',
                    note: contact?.note || ''
                }}
                style={{ width: '100%', overflowX: 'hidden' }}
            >
                {/* Profile Picture Upload */}
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Space align="center" size={16}>
                            <Avatar size={64} src={photoUrl} data-file-upload-id={photoUploadId || undefined}>
                                {!photoUrl && `${contact?.first_name?.[0] || ''}${contact?.last_name?.[0] || ''}`}
                            </Avatar>

                            {/* Both web and native: Use ImgCrop for consistent cropping experience */}
                            <ImgCrop
                                rotationSlider
                                zoomSlider
                                aspect={1}
                                quality={0.9}
                                modalTitle={t('edit_photo')}
                                shape="round"
                            >
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                >
                                    <Button
                                        shape="circle"
                                        icon={isNative ? <CameraOutlined /> : <EditOutlined />}
                                        loading={uploadingPhoto}
                                        aria-label={t('change_photo')}
                                    />
                                </Upload>
                            </ImgCrop>

                            {photoUrl && (
                                <Popconfirm
                                    title={t('delete_profile_picture')}
                                    onConfirm={async () => {
                                        try {
                                            setDeletingProfilePicture(true);
                                            await ContactsServices.deleteContactProfilePicture(contact.id);
                                            setPhotoUrl(undefined);
                                            setPhotoUploadId(null);
                                            form.setFieldsValue({ picture_url: '' });
                                            message.success(t('profile_picture_deleted_successfully'));
                                            if (typeof onSuccess === 'function') {
                                                try {
                                                    onSuccess();
                                                } catch (e) { }
                                            }
                                        } catch (e) {
                                            message.error(t('failed_to_delete_profile_picture'));
                                        } finally {
                                            setDeletingProfilePicture(false);
                                        }
                                    }}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                >
                                    <Button danger shape="circle" loading={deletingProfilePicture} icon={<DeleteOutlined />} aria-label={t('delete_profile_picture')} />
                                </Popconfirm>
                            )}
                        </Space>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            disabled
                            name="contact_type"
                            label={t('contact_type')}
                            rules={[{ required: true, message: t('please_select_contact_type') }]}
                        >
                            <Select disabled>
                                <Option value="customer">{t('customer')}</Option>
                                <Option value="admin">{t('admin')}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="gender"
                            label={t('gender')}
                        >
                            <Select>
                                <Option value="male">{t('male')}</Option>
                                <Option value="female">{t('female')}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="first_name"
                            label={t('first_name')}
                            rules={[{ required: true, message: t('please_enter_first_name') }]}
                        >
                            <Input placeholder={t('enter_first_name')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="last_name"
                            label={t('last_name')}
                            rules={[{ required: true, message: t('please_enter_last_name') }]}
                        >
                            <Input placeholder={t('enter_last_name')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="email"
                            label={t('email')}
                            rules={[
                                { type: 'email', message: t('please_enter_valid_email') },
                                {
                                    validator: (_, value) => {
                                        if (!value || (value && value.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/))) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('please_enter_valid_email')));
                                    }
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_email')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="phone"
                            label={t('phone')}
                            rules={[
                                {
                                    pattern: phonePattern,
                                    message: t('please_enter_valid_phone'),
                                    validator: (_, value) => {
                                        if (!value || phonePattern.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('please_enter_valid_phone')));
                                    }
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_phone')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="locale"
                            label={t('locale')}
                            rules={[
                                {
                                    pattern: /^[a-z]{2}(_[A-Z]{2})?$/,
                                    message: t('please_enter_valid_locale'),
                                }
                            ]}
                        >
                            <Input placeholder={t('enter_locale')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="timezone"
                            label={t('timezone')}
                            rules={[
                                {
                                    type: 'number',
                                    min: -12,
                                    max: 14,
                                    message: t('please_enter_valid_timezone'),
                                    transform: (value) => {
                                        if (value === '') return null;
                                        return Number(value);
                                    }
                                }
                            ]}
                        >
                            <Input type="number" min={-12} max={14} placeholder={t('enter_timezone')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[{ xs: 4, sm: 8, md: 16 }, 8]} style={rowStyle}>
                    <Col xs={24}>
                        <Form.Item
                            name="note"
                            label={t('note')}
                        >
                            <Input.TextArea rows={4} placeholder={t('enter_note')} dir="auto" style={{ unicodeBidi: 'plaintext' }} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </ResponsiveModal>
    );
};

export default ContactEditModal;


